/**
 * Insurance & fees (owner feedback 2026-10-21: "questionable whether that
 * should be buried in the settings"). Fee schedules and insurance plans are
 * daily billing tools, so they have their own module under Billing &
 * insurance, with tabs (app/layout/familyTabs.ts): Insurance plans,
 * Carriers, Fee schedules, Coverage and Frequency limits.
 *
 * The pages are the ones Settings had (InsurancePlansPage, and
 * settings/pages/FeeSchedulesPage drawn in this module's frame); the old
 * Settings paths `/settings/insurance-plans` and `/settings/fee-schedules`
 * redirect here so bookmarks keep working. Permissions are unchanged:
 * plans need `insurance.view` (edits `insurance.edit`), fee schedules open
 * for anyone signed in and edit with `settings.manage`.
 *
 * PMS plans are read-only and say "Edit it in <PMS>": Velora never writes
 * insurance plans to a PMS. API routes under `/v1/insurance-plans` and
 * `/v1/settings/fee-schedules` (unchanged).
 */
import { defineManifest } from '@velora/contracts';
import { INSURANCE_PLAN_PERMISSIONS as P } from '@velora/contracts/insurance-plans';
import { defineWebModule } from '../../app/module.js';

export const INSURANCE_FEES_BASE = '/insurance-fees';

export default defineWebModule({
  manifest: defineManifest({
    id: 'insurance-plans',
    title: 'Insurance & fees',
    version: '0.2.0',
    owner: 'WP4-ELIG',
    entitlementKey: null,
    permissions: [P.view, P.edit, 'money.view'],
    routes: [
      {
        id: 'plans',
        path: '/insurance-fees',
        title: 'Insurance plans',
        officeScope: 'practice',
        permission: P.view,
      },
      {
        id: 'carriers',
        path: '/insurance-fees/carriers',
        title: 'Insurance carriers',
        officeScope: 'practice',
        permission: P.view,
      },
      {
        // Same rule as the Settings page it replaces: anyone may look,
        // `settings.manage` edits (checked on the page and by the API).
        id: 'fees',
        path: '/insurance-fees/fee-schedules',
        title: 'Fee schedules',
        officeScope: 'practice',
      },
      {
        id: 'coverage',
        path: '/insurance-fees/coverage',
        title: 'Coverage by plan',
        officeScope: 'practice',
        permission: P.view,
      },
      {
        id: 'frequency',
        path: '/insurance-fees/frequency-limits',
        title: 'Frequency limits',
        officeScope: 'practice',
        permission: P.view,
      },
      {
        // Marketplace `ppo_analysis` (owner feedback 2026-10-28): discount vs
        // UCR by category and overall, patients per plan, drop estimate.
        id: 'ppo',
        path: '/insurance-fees/ppo-analysis',
        title: 'PPO analysis',
        officeScope: 'location-or-all',
        permission: 'money.view',
        entitlementKey: 'ppo_analysis',
      },
      {
        // Old Settings address (bookmarks, the patient's Edit insurance link).
        id: 'legacy',
        path: '/settings/insurance-plans',
        title: 'Insurance plans and carriers',
        officeScope: 'practice',
        permission: P.view,
      },
    ],
    nav: [
      {
        label: 'Insurance & fees',
        group: 'revenue',
        routeId: 'plans',
        icon: 'receipt',
        order: 40,
        permission: P.view,
      },
    ],
    commands: [
      {
        id: 'insurance-plans.open',
        title: 'Insurance plans and carriers',
        keywords: [
          'insurance plan',
          'edit plan',
          'carrier',
          'coverage table',
          'deductible',
          'annual max',
          'maximum',
          'frequency',
          'payor id',
          'payer id',
          'benefits',
          'insurance & fees',
        ],
        routeId: 'plans',
        permission: P.view,
      },
      {
        id: 'insurance-plans.fees',
        title: 'Fee schedules',
        keywords: ['fees', 'ucr', 'ppo', 'prices', 'fee schedule', 'allowed'],
        routeId: 'fees',
      },
      {
        id: 'insurance-plans.ppo',
        title: 'PPO analysis: discount vs UCR, patients per plan',
        keywords: [
          'ppo',
          'ppo analysis',
          'discount',
          'write-off',
          'ucr',
          'drop ppo',
          'delta',
          'metlife',
          'patients per plan',
          'fee schedule by category',
        ],
        routeId: 'ppo',
        permission: 'money.view',
      },
      {
        id: 'insurance-plans.coverage',
        title: 'Coverage by plan (percent by category)',
        keywords: ['coverage', 'percent', 'preventive', 'basic', 'major', 'benefits'],
        routeId: 'coverage',
        permission: P.view,
      },
      {
        id: 'insurance-plans.frequency',
        title: 'Frequency limits by plan',
        keywords: ['frequency', 'limits', 'bwx', 'fmx', 'prophy', 'once every'],
        routeId: 'frequency',
        permission: P.view,
      },
    ],
    sseEntityKeys: ['ins_plan', 'carrier', 'fee_schedule'],
    phiReadPoints: ['insurance.plans'],
  }),
  pages: {
    plans: () => import('./InsurancePlansPage.js'),
    carriers: () => import('./CarriersPage.js'),
    fees: () => import('./FeesPage.js'),
    coverage: () => import('./CoveragePage.js'),
    frequency: () => import('./FrequencyPage.js'),
    ppo: () => import('./PpoAnalysisPage.js'),
    legacy: () => import('./LegacyRedirect.js'),
  },
});
